import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { fromEvent } from 'rxjs';
import { initialRootState } from '../root-store/root.state';
import { AuthenticationService } from './authentication/authentication.service';
import { selectSeReportCurrentExaminationIds } from '../report/report.selectors';
import { WpfService } from '../connectivity/wpf.service';
import { InMemoryTokenStore, ServerRootDetector } from '@se/se.ui.html5';
import { jsonBlob } from '../utils/utils';
import { TpIntegrationsCacheService } from './tp-integrations/tp-integrations-cache.service';

export type OnUnloadEventHandler = (event: BeforeUnloadEvent) => string;

@Injectable({
    providedIn: 'root',
})
export class PageUnloadService {
    private callbacks: OnUnloadEventHandler[] = [];
    private isForced = false;

    // Caso particolare in cui lo store dev'essere sincrono: ci serve all'unload della pagina,
    //   non possiamo aspettare un timeout perché la pagina verrebbe chiusa.
    private currentExaminationIds = initialRootState.report.currentExaminationIds;

    constructor(
        private authentication: AuthenticationService,
        private store: Store,
        private tokenStore: InMemoryTokenStore,
        private tpIntegrationsCache: TpIntegrationsCacheService,
        private wpf: WpfService,
        @Inject(DOCUMENT) document: Document,
    ) {
        fromEvent(document.defaultView, 'beforeunload').subscribe((event) => this.onBeforeUnload(event));
        fromEvent(document.defaultView, 'unload').subscribe(() => this.onUnload());

        this.store
            .pipe(select(selectSeReportCurrentExaminationIds))
            .subscribe((ids) => (this.currentExaminationIds = ids));
    }

    setIsForced(isForced: boolean) {
        this.isForced = isForced;
    }

    register(cb: OnUnloadEventHandler) {
        this.callbacks.push(cb);
    }

    unregister(cb: OnUnloadEventHandler) {
        this.callbacks = this.callbacks.filter((callback) => callback != cb);
    }

    private onBeforeUnload(event: BeforeUnloadEvent) {
        if (this.isForced) {
            return;
        }
        for (const callback of this.callbacks) {
            const result = callback(event);
            if (result) {
                event.preventDefault();
                event.returnValue = result;
                return result;
            }
        }
    }

    private onUnload() {
        // Nessuno ha nulla in contrario alla chiusura...
        if (this.authentication.isOpenedFromWpf) {
            this.wpf.webClientClosing('onUnload');
        }

        return navigator.sendBeacon(
            ServerRootDetector.getInstance().getResourceRoot() + '/Authentication/PageUnload',
            jsonBlob({
                // Salta il logout se aperto da WPF o da un altro tab.
                logout: !this.authentication.hasSecondaryAuthentication,
                token: this.tokenStore.getToken(),
                ECG_Unlock_Examinations: this.currentExaminationIds.ecg,
                Visit_Unlock_Examinations: this.currentExaminationIds.visit,
                oidcToken: (this.tpIntegrationsCache.get('sso-token') as any)?.id_token,
            }),
        );
    }
}
